import { useState, useEffect, useContext, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Chip, Avatar } from "@mui/material";
import { AuthContext } from "../../../Context/AuthContext";
import {
  SELECT_USER,
  SELECT_CLASS,
  SELECT_COURSE,
  SELECT_SUBJECT,
  SELECT_STUDENT,
  SELECT_EXAM_TYPE,
  SELECT_GROUP_FEE,
  SELECT_CLASS_BY_STUDENT_ID,
  SELECT_SUBJECT_BY_CLASS_ID,
  SELECT_ENROLLMENT_BY_STUDENT,
  SELECT_SCHEDULE_BY_CLASS,
  SELECT_PERFORMANCE_TYPE,
  SELECT_STUDENT_BY_CLASS,
  SELECT_CONTENT,
  SELECT_PACKAGE_PRICE,
  SELECT_ONLINE_CLASS,
  SELECT_BOOK_GROUP_FEE,
  SELECT_ONLINE_CLASS_PACKAGE,
  SELECT_CLASS_GROUP_FEE,
  SELECT_LESSON,
} from "../../../Schema/SelectSchema";
import { GET_STUDENT_BY_CLASS_ID } from "../../../Schema/Class";
import { GET_SHIFT } from "../../../Schema/Shift";
import ShiftForm from "../../Shift/ShiftForm";
import TeacherForm from "../../Teacher/TeacherForm";
import GroupFeeForm from "../../GroupFee/GroupFeeForm";
import CourseForm from "../../Course/CourseForm";
import StudentForm from "../../Student/StudentForm";
import SubjectForm from "../../Subject/SubjectForm";
import ExamTypeForm from "../../StudyClass/Exam/ExamTypeForm/ExamTypeForm";

import {
  Stack,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
} from "@mui/material";

import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import PerformanceTypeForm from "../../PerformanceType/PerformanceTypeForm";
import { GET_ALL_EXPENSE_TYPE } from "../../../Schema/Expense";
import ExpenseTypeForm from "../../ExpenseType/ExpenseTypeForm";

export const SelectUser = ({
  className,
  onChange,
  buttonAdd = true,
  value = { id: "", title: "" },
  role,
}) => {
  // Change Language
  const { t } = useContext(AuthContext);

  const [selectOptions, setSelectOptions] = useState([]);
  const { refetch } = useQuery(SELECT_USER, {
    variables: {
      role: role,
    },
    onCompleted: ({ selectUser }) => {
      const data = selectUser?.map((e) => ({
        id: e?._id,
        title: e?.familyName + " " + e?.userName,
      }));
      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        <Typography className={t("class-name-field-title")}>
          {role === "Teacher" ? t("thead_teacher") : t("thead_user")}
        </Typography>
        {buttonAdd && (
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        )}
        {open && (
          <TeacherForm
            hideBackdrop={true}
            open={open}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
          />
        )}
      </Stack>
      <Autocomplete
        fullWidth
        size="small"
        options={selectOptions}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField {...params} placeholder={t("thead_select")} />
        )}
      />
    </Stack>
  );
};

export const SelectCourse = ({
  className,
  onChange,
  buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  const { refetch } = useQuery(SELECT_COURSE, {
    onCompleted: ({ selectCourse }) => {
      // console.log("selectCourse:", selectCourse);
      const data = selectCourse?.map((e) => ({
        id: e?._id,
        title: language === "en" ? e?.enName : e?.khName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        {buttonAdd && (
          <Typography className={t("class-name-field-title")}>
            {t("thead_course")}
          </Typography>
        )}
        {buttonAdd && (
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        )}
        {open && (
          <CourseForm
            hideBackdrop={true}
            open={open}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
          />
        )}
      </Stack>
      <Autocomplete
        fullWidth
        size="small"
        options={selectOptions}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField {...params} placeholder={t("thead_select")} />
        )}
      />
    </Stack>
  );
};

export const SelectShift = ({
  className,
  onChange,
  value = { id: "", title: "" },
  buttonAdd = true,
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  const { refetch } = useQuery(GET_SHIFT, {
    onCompleted: ({ getShifts }) => {
      // console.log("getShifts:", getShifts);
      const data = getShifts?.map((e) => ({
        id: e?._id,
        title: language === "en" ? e?.enName : e?.khName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        <Typography className={t("class-name-field-title")}>
          {t("thead_shift")}
        </Typography>
        {buttonAdd && (
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        )}
        {open && (
          <ShiftForm
            hideBackdrop={true}
            open={open}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
          />
        )}
      </Stack>

      <Autocomplete
        fullWidth
        size="small"
        options={selectOptions}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField {...params} placeholder={t("thead_select")} />
        )}
      />
    </Stack>
  );
};

export const SelectClass = ({
  checkSchedule,
  setSelecteSchedule,
  className,
  onChange,
  disabled,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  // console.log("Selecclass", selectOptions)
  const { refetch } = useQuery(SELECT_CLASS, {
    onCompleted: ({ selectClass }) => {
      const data = selectClass?.map((e) => ({
        id: e?._id,
        title:
          language === "en"
            ? e?.courseId?.enName + " " + e?.className
            : e?.courseId?.khName + " " + e?.className,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const handleChange = (val) => {
    onChange(val);
    if (checkSchedule) {
      setSelecteSchedule([]);
    }
  };

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => handleChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectClassByStudentId = ({
  className,
  onChange,
  studentId,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  const { refetch } = useQuery(SELECT_CLASS_BY_STUDENT_ID, {
    variables: {
      studentId,
    },
    onCompleted: ({ getClassByStudentId }) => {
      const data = getClassByStudentId?.map((e) => ({
        id: e?._id,
        title:
          language === "en"
            ? e?.courseId?.enName + " " + e?.className
            : e?.courseId?.enName + " " + e?.className,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectSubject = ({
  className,
  onChange,
  error,
  dataOptions,
  buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  const { refetch } = useQuery(SELECT_SUBJECT, {
    onCompleted: ({ selectSubject }) => {
      const data = selectSubject?.map((e) => ({
        id: e?._id,
        title: language === "en" ? e?.enName : e?.khName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        {buttonAdd && (
          <Typography className={t("class-name-field-title")}>
            {t("thead_subject")}
          </Typography>
        )}
        {buttonAdd && (
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        )}
        {open && (
          <SubjectForm
            open={open}
            hideBackdrop={true}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
          />
        )}
      </Stack>

      <Autocomplete
        fullWidth
        size="small"
        options={selectOptions.filter(
          (e) => !dataOptions?.map((e) => e?.subjectId?.id)?.includes(e?.id),
        )}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("thead_select")}
            error={error}
          />
        )}
      />
    </Stack>
  );
};

export const SelectSubjectByClassId = ({
  className,
  onChange,
  error,
  classId,
  dataOptions,
  value = { id: "", title: "" },
  disabled,
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_SUBJECT_BY_CLASS_ID, {
    variables: {
      classId: classId,
    },
    onCompleted: ({ selectSubjectByClassId }) => {
      const data = selectSubjectByClassId?.map((e) => ({
        id: e?._id,
        title: language === "en" ? e?.enName : e?.khName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  // console.log("selectOptions::", selectOptions)

  useEffect(() => {
    refetch();
  }, [value]);

  return (
    <Autocomplete
      disabled={disabled ? true : false}
      fullWidth
      size="small"
      options={selectOptions.filter(
        (e) => !dataOptions?.map((e) => e?.subjectId?.id)?.includes(e?.id),
      )}
      className={className}
      value={value}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} error={error} />
      )}
    />
  );
};

export const SelectExamType = ({
  classId,
  className,
  onChange,
  data,
  buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_EXAM_TYPE, {
    variables: { classId },
    onCompleted: ({ selectExamType }) => {
      const data = selectExamType?.map((e) => ({
        id: e?._id,
        title: e?.examName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        {buttonAdd && (
          <Typography className={t("class-name-text-field-title")}>
            {t("thead_exam_type")}
          </Typography>
        )}
        {buttonAdd && (
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        )}
        {open && (
          <ExamTypeForm
            hideBackdrop={true}
            open={open}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
            classId={classId}
          />
        )}
      </Stack>

      <Autocomplete
        fullWidth
        size="small"
        // options={selectOptions}
        options={selectOptions.filter(
          (e) => !data?.map((e) => e?.subjectId?.id)?.includes(e?.id),
        )}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField {...params} placeholder={t("thead_select")} />
        )}
      />
    </Stack>
  );
};

export const SelectExamTypeForForm = ({
  classId,
  className,
  onChange,
  data,
  buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_EXAM_TYPE, {
    variables: { classId },
    onCompleted: ({ selectExamType }) => {
      const data = selectExamType?.map((e) => ({
        id: e?._id,
        title: e?.examName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        <Typography className={t("class-name-field-title")}>
          {t("thead_exam_type")}
        </Typography>
        <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
          <AddBoxOutlinedIcon
            fontSize="small"
            sx={{ color: "green", cursor: "pointer" }}
          />
        </IconButton>
        {open && (
          <ExamTypeForm
            hideBackdrop={true}
            open={open}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
            classId={classId}
          />
        )}
      </Stack>

      <Autocomplete
        fullWidth
        size="small"
        // options={selectOptions}
        options={selectOptions.filter(
          (e) => !data?.map((e) => e?.subjectId?.id)?.includes(e?.id),
        )}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField {...params} placeholder={t("thead_select")} />
        )}
      />
    </Stack>
  );
};

export const SelectStudent = ({
  studentType,
  className,
  onChange,
  data,
  disabled,
  buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  //
  const { refetch } = useQuery(SELECT_STUDENT, {
    variables: { studentType },
    onCompleted: ({ selectStudent }) => {
      const data = selectStudent?.map((e) => ({
        id: e?._id,
        title: e?.familyName + " " + e?.studentName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [studentType]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // console.log(value); // 👈 prevent undefined)

  return (
    <Stack>
      {buttonAdd && (
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
        >
          <Typography className={t("class-name-field-title")}>
            {t("thead_student")}
          </Typography>

          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>

          {open && (
            <StudentForm
              hideBackdrop={true}
              open={open}
              setRefetch={refetch}
              dialogTitle={"Create"}
              handleClose={handleClose}
            />
          )}
        </Stack>
      )}

      <Autocomplete
        disabled={disabled}
        fullWidth
        size="small"
        options={selectOptions.filter(
          (e) => !data?.map((d) => d?.studentId?.id)?.includes(e?.id),
        )}
        className={className}
        value={value ?? null} // 👈 prevent undefined
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option?.title ?? ""} // 👈 prevent error if null
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField {...params} placeholder={t("thead_select")} />
        )}
      />
    </Stack>
  );
};

export const SelectStudentByClass = ({
  className,
  onChange,
  classId,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  const { refetch } = useQuery(GET_STUDENT_BY_CLASS_ID, {
    variables: {
      keyword: "",
      classId: classId,
    },
    onCompleted: ({ getStudentByclassId }) => {
      const data = getStudentByclassId?.map((e) => ({
        id: e?._id,
        title: e?.familyName + " " + e?.studentName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectStudentByClassForTeachingTime = ({
  className,
  onChange,
  classId,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_STUDENT_BY_CLASS, {
    variables: {
      classId: classId,
    },
    onCompleted: ({ getStudentByClassForSelect }) => {
      const data = getStudentByClassForSelect?.map((e) => ({
        id: e?._id,
        title: e?.familyName + " " + e?.studentName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectGroupFee = ({
  className,
  onChange,
  error,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_GROUP_FEE, {
    onCompleted: ({ selectGroupFee }) => {
      // console.log("SelectGroupFee", selectGroupFee)
      const data = selectGroupFee?.map((e) => ({
        id: e?._id,
        title: `${e?.title}: ${e?.price}$`,
        price: e?.price,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} error={error} />
      )}
    />
  );
};

export const SelectBookGroupFee = ({
  className,
  onChange,
  error,
  buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_BOOK_GROUP_FEE, {
    onCompleted: ({ selectBookGroupFee }) => {
      // console.log("selectBookGroupFee::", selectBookGroupFee)
      const data = selectBookGroupFee?.map((e) => ({
        id: e?._id,
        title: `${e?.title}: ${e?.price}$`,
        price: e?.price,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      {buttonAdd && (
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
        >
          <Typography className={t("class-name-field-title")}>
            {t("page_group_fee")}
          </Typography>
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        </Stack>
      )}
      {open && (
        <GroupFeeForm
          open={open}
          setRefetch={refetch}
          dialogTitle={"Create"}
          handleClose={handleClose}
        />
      )}

      <Autocomplete
        fullWidth
        size="small"
        options={selectOptions}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("thead_select")}
            error={error}
          />
        )}
      />
    </Stack>
  );
};

export const SelectClassGroupFee = ({
  className,
  onChange,
  error,
  buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_CLASS_GROUP_FEE, {
    onCompleted: ({ selectClassGroupFee }) => {
      // console.log("selectClassGroupFee::", selectClassGroupFee)
      const data = selectClassGroupFee?.map((e) => ({
        id: e?._id,
        title: `${e?.title}: ${e?.price}$`,
        price: e?.price,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      {buttonAdd && (
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
        >
          <Typography className={t("class-name-field-title")}>
            {t("thead_group_fee")}
          </Typography>
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        </Stack>
      )}
      {open && (
        <GroupFeeForm
          open={open}
          setRefetch={refetch}
          dialogTitle={"Create"}
          handleClose={handleClose}
        />
      )}

      <Autocomplete
        fullWidth
        size="small"
        options={selectOptions}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("thead_select")}
            error={error}
          />
        )}
      />
    </Stack>
  );
};

export const SelectEnrollmentByStudent = ({
  className,
  onChange,
  studentId,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_ENROLLMENT_BY_STUDENT, {
    variables: {
      studentId: studentId || "",
    },
    onCompleted: ({ selectEnrollmentByStudent }) => {
      const data = selectEnrollmentByStudent?.map((e) => ({
        id: e?._id,
        title: `${
          language === "en"
            ? e?.classId?.courseId?.enName
            : e?.classId?.courseId?.khName
        } ${e?.classId?.className}`,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectScheduleByClass = ({
  classId,
  className,
  onChange,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  // console.log("classId::", classId);

  const { refetch } = useQuery(SELECT_SCHEDULE_BY_CLASS, {
    variables: { classId },
    onCompleted: ({ selectScheduleByClass }) => {
      // console.log("selectScheduleByClass::", selectScheduleByClass);
      const data = selectScheduleByClass?.map((e) => ({
        id: e?._id,
        title: e?.day + " (" + e?.startTime + "-" + e?.endTime + ")",
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value, classId]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectmultiScheduleByClass = ({ classId, value, onChange }) => {
  // Change Language
  const { t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  // console.log("classId::", classId);

  const { refetch } = useQuery(SELECT_SCHEDULE_BY_CLASS, {
    variables: { classId },
    onCompleted: ({ selectScheduleByClass }) => {
      // console.log("selectScheduleByClass::", selectScheduleByClass);
      const data = selectScheduleByClass?.map((e) => ({
        id: e?._id,
        title: e?.day + " (" + e?.startTime + "-" + e?.endTime + ")",
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value, classId]);

  return (
    <Autocomplete
      multiple
      size="small"
      id="multiple-schedule-select"
      options={selectOptions}
      value={value}
      onChange={onChange}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            key={option.id}
            variant="outlined"
            color="warning"
            label={option.title}
            {...getTagProps({ index })}
            sx={{ borderRadius: 1 }}
          />
        ))
      }
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.id}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          {option.title}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectPerformanceType = ({
  className,
  onChange,
  error,
  dataOptions,
  buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  // Change Language

  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_PERFORMANCE_TYPE, {
    onCompleted: ({ selectPerformanceType }) => {
      const data = selectPerformanceType?.map((e) => ({
        id: e?._id,
        title: language === "en" ? e?.enName : e?.khName,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        {buttonAdd && (
          <Typography className={t("class-name-field-title")}>
            {t("page_performance_type")}
          </Typography>
        )}
        {buttonAdd && (
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        )}
        {open && (
          <PerformanceTypeForm
            open={open}
            hideBackdrop={true}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
          />
        )}
      </Stack>

      <Autocomplete
        fullWidth
        size="small"
        options={selectOptions.filter(
          (e) => !dataOptions?.map((e) => e?.subjectId?.id)?.includes(e?.id),
        )}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("thead_select")}
            error={error}
          />
        )}
      />
    </Stack>
  );
};

export const SelectExpenseType = ({
  buttonAdd = true,
  className,
  onChange,
  // error,
  dataOptions,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(GET_ALL_EXPENSE_TYPE, {
    onCompleted: ({ getAllExpenseType }) => {
      const data = getAllExpenseType?.map((e) => ({
        id: e?._id,
        title: language === "en" ? e?.engType : e?.khType,
      }));
      setSelectOptions(data || []);
    },
    onError: (err) => {
      console.log("Error::", err?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        {buttonAdd && (
          <Typography className={t("class-name-field-title")}>
            {t("thead_type")}
          </Typography>
        )}
        {buttonAdd && (
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        )}
        {open && (
          <ExpenseTypeForm
            open={open}
            hideBackdrop={true}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
          />
        )}
      </Stack>

      <Autocomplete
        fullWidth
        size="small"
        options={selectOptions.filter(
          (e) => !dataOptions?.map((e) => e?.subjectId?.id)?.includes(e?.id),
        )}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("thead_select")}
            // error={error}
          />
        )}
      />
    </Stack>
  );
};

export const SelectContent = ({
  className,
  onChange,
  value = { id: "", title: "" },
}) => {
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  const { refetch } = useQuery(SELECT_CONTENT, {
    onCompleted: ({ selectContent }) => {
      const data = selectContent?.map((e) => ({
        id: e?._id,
        title: e?.title,
      }));
      setSelectOptions(data || []);
    },
    onError: (err) => {
      console.log("Error::", err?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  return (
    <Autocomplete
      fullWidth
      // disabled={disabled}
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectMultiContent = ({
  className,
  onChange,
  classId,
  value = [],
  placeholder,
}) => {
  const { t } = useContext(AuthContext);

  const { data, loading } = useQuery(
    SELECT_CONTENT,
    { variables: { classId: classId } },
    {
      fetchPolicy: "cache-first",
    },
  );

  // console.log("data::", classId, data)

  // Derive options directly from query data
  const selectOptions = useMemo(() => {
    if (!data?.selectContent) return [];
    return data.selectContent.map((e) => ({
      id: e?._id,
      title: e?.title || "",
    }));
  }, [data]);

  // console.log("selectOptions::", selectOptions)

  const selectedValues = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value.map((val) => {
      if (typeof val === "object" && val !== null) return val;
      return (
        selectOptions.find((opt) => opt.id === val) || {
          id: val,
          title: val,
        }
      );
    });
  }, [value, selectOptions]);

  return (
    <Autocomplete
      multiple
      size="small"
      loading={loading}
      className={className}
      options={selectOptions}
      value={selectedValues}
      onChange={(_, selectedOptions) => {
        if (typeof onChange === "function") {
          onChange(selectedOptions);
        }
      }}
      getOptionLabel={(option) => option?.title || ""}
      isOptionEqualToValue={(option, val) => option.id === (val.id || val)}
      renderTags={(valList, getTagProps) =>
        valList.map((option, index) => (
          <Chip
            key={option.id || index}
            variant="fill"
            label={option.title}
            {...getTagProps({ index })}
            sx={{ borderRadius: 10 }}
          />
        ))
      }
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.id}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          {option.title}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            selectedValues.length === 0 ? placeholder || t("thead_select") : ""
          }
        />
      )}
    />
  );
};

export const SelectMultiLesson = ({
  className,
  onChange,
  classId,
  value = [],
  placeholder,
}) => {
  const { t } = useContext(AuthContext);

  const { data, loading } = useQuery(
    SELECT_LESSON,
    { variables: { classId: classId } },
    {
      fetchPolicy: "cache-first",
    },
  );

  // Derive options directly from query data
  const selectOptions = useMemo(() => {
    if (!data?.selectLesson) return [];
    return data.selectLesson.map((e) => ({
      id: e?._id,
      title: e?.title || "",
    }));
  }, [data]);

  // console.log("selectOptions::", selectOptions)

  const selectedValues = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value.map((val) => {
      if (typeof val === "object" && val !== null) return val;
      return (
        selectOptions.find((opt) => opt.id === val) || {
          id: val,
          title: val,
        }
      );
    });
  }, [value, selectOptions]);

  return (
    <Autocomplete
      multiple
      size="small"
      loading={loading}
      className={className}
      options={selectOptions}
      value={selectedValues}
      onChange={(_, selectedOptions) => {
        if (typeof onChange === "function") {
          onChange(selectedOptions);
        }
      }}
      getOptionLabel={(option) => option?.title || ""}
      isOptionEqualToValue={(option, val) => option.id === (val.id || val)}
      renderTags={(valList, getTagProps) =>
        valList.map((option, index) => (
          <Chip
            key={option.id || index}
            variant="fill"
            label={option.title}
            {...getTagProps({ index })}
            sx={{ borderRadius: 10 }}
          />
        ))
      }
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.id}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          {option.title}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            selectedValues.length === 0 ? placeholder || t("thead_select") : ""
          }
        />
      )}
    />
  );
};

// export const SelectMultiPackagePrice = ({
//   className,
//   onChange,
//   value = [],
//   placeholder,
// }) => {
//   const { t } = useContext(AuthContext);

//   const { data, loading } = useQuery(SELECT_PACKAGE_PRICE, {
//     fetchPolicy: "cache-first",
//   });
//   // console.log("multiSelectPackagePrice:", data)
//   const selectOptions = useMemo(() => {
//     if (!data?.selectPackagePrice) return [];
//     return data.selectPackagePrice.map((e) => ({
//       id: e?._id,
//       // title: `${e.title} ${e?.classId?.courseId?.enName || e?.classId?.className || ""} - ${e?.groupFeeId?.title + ": " || ""}
//       // ${e?.groupFeeId?.price ? `${e?.groupFeeId.price}$` : ""}`
//       title: `${e.title} ${e?.classId?.courseId?.enName || e?.classId?.className || ""} - ${e?.groupFeeId?.title + ": " || ""}
//       ${e?.groupFeeId?.price} $`,
//     }));
//   }, [data]);

//   const selectedValues = useMemo(() => {
//     if (!Array.isArray(value)) return [];
//     return value.map((val) => {
//       if (typeof val === "object" && val !== null) return val;
//       return (
//         selectOptions.find((opt) => opt.id === val) || {
//           id: val,
//           title: val,
//         }
//       );
//     });
//   }, [value, selectOptions]);
//   // console.log("selectionValues::", selectedValues)

//   return (
//     <Autocomplete
//       multiple
//       size="small"
//       loading={loading}
//       className={className}
//       options={selectOptions}
//       value={selectedValues}
//       onChange={(_, selectedOptions) => {
//         if (typeof onChange === "function") {
//           onChange(selectedOptions);
//         }
//       }}
//       getOptionLabel={(option) => option?.title || ""}
//       isOptionEqualToValue={(option, val) => option.id === (val.id || val)}
//       renderTags={(valList, getTagProps) =>
//         valList.map((option, index) => (
//           <Chip
//             key={option.id || index}
//             variant="fill"
//             label={option.title}
//             {...getTagProps({ index })}
//             sx={{ borderRadius: 10 }}
//           />
//         ))
//       }
//       renderOption={(props, option) => (
//         <li
//           {...props}
//           key={option.id}
//           style={{ display: "flex", alignItems: "center", gap: 8 }}
//         >
//           {option.title}
//         </li>
//       )}
//       renderInput={(params) => (
//         <TextField
//           {...params}
//           placeholder={
//             selectedValues.length === 0 ? placeholder || t("thead_select") : ""
//           }
//         />
//       )}
//     />
//   );
// };

export const SelectOnlineClass = ({
  checkSchedule,
  setSelecteSchedule,
  className,
  onChange,
  disabled,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);
  // console.log("selectOnlineClass", selectOptions)
  const { refetch } = useQuery(SELECT_ONLINE_CLASS, {
    onCompleted: ({ selectOnlineClass }) => {
      const data = selectOnlineClass?.map((e) => ({
        id: e?._id,
        title:
          language === "en"
            ? e?.courseId?.enName + " " + e?.className
            : e?.courseId?.khName + " " + e?.className,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const handleChange = (val) => {
    onChange(val);
    if (checkSchedule) {
      setSelecteSchedule([]);
    }
  };

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => handleChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectOnlineClassPackage = ({
  checkSchedule,
  setSelecteSchedule,
  className,
  onChange,
  disabled,
  value = { id: "", title: "" },
}) => {
  // Change Language
  const { language, t } = useContext(AuthContext);
  const [selectOptions, setSelectOptions] = useState([]);

  const { refetch } = useQuery(SELECT_ONLINE_CLASS_PACKAGE, {
    onCompleted: ({ selectOnlineClassForPackagePrice }) => {
      const data = selectOnlineClassForPackagePrice?.map((e) => ({
        id: e?._id,
        title:
          language === "en"
            ? e?.courseId?.enName + " " + e?.className
            : e?.courseId?.khName + " " + e?.className,
      }));

      setSelectOptions(data || []);
    },
    onError: (error) => {
      console.log(error?.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [value]);

  const handleChange = (val) => {
    onChange(val);
    if (checkSchedule) {
      setSelecteSchedule([]);
    }
  };

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      size="small"
      options={selectOptions}
      className={className}
      value={value}
      onChange={(event, val) => handleChange(val)}
      getOptionLabel={(option) => option.title || ""}
      isOptionEqualToValue={(option, value) => true}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("thead_select")} />
      )}
    />
  );
};

export const SelectPackagePriceByClass = ({
  className,
  classId,
  onChange,
  // buttonAdd = true,
  value = { id: "", title: "" },
}) => {
  const { t } = useContext(AuthContext);

  const { refetch, data } = useQuery(SELECT_PACKAGE_PRICE, {
    variables: { classId: classId },
    fetchPolicy: "cache-first",
  });
  // console.log("multiSelectPackagePrice:", data)
  const selectOptions = useMemo(() => {
    if (!data?.selectPackagePrice) return [];
    return data.selectPackagePrice.map((e) => ({
      id: e?._id,
      title: `${e.title} - ${e?.groupFeeId?.price} $`,
      price: e?.groupFeeId?.price,
    }));
  }, [data]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        {/* <Typography className={t("class-name-field-title")}>
          {t("thead_user")}
        </Typography> */}
        {/* {buttonAdd && (
          <IconButton sx={{ padding: 0.5 }} onClick={handleOpen}>
            <AddBoxOutlinedIcon
              fontSize="small"
              sx={{ color: "green", cursor: "pointer" }}
            />
          </IconButton>
        )} */}
        {open && (
          <TeacherForm
            hideBackdrop={true}
            open={open}
            setRefetch={refetch}
            dialogTitle={"Create"}
            handleClose={handleClose}
          />
        )}
      </Stack>
      <Autocomplete
        fullWidth
        // disabled={disabled}
        size="small"
        options={selectOptions}
        className={className}
        value={value}
        onChange={(event, val) => onChange(val)}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => true}
        renderInput={(params) => (
          <TextField {...params} placeholder={t("thead_select")} />
        )}
      />
    </Stack>
  );
};
